import React from "react";

export type ProducaoToggleEstado =
  | "pendente"
  | "produzido"
  | "nao_produzido"
  | "nao_produzido_unidade"
  | "assinada";

type ProducaoToggleProps = {
  checked: boolean;
  estado: ProducaoToggleEstado;
  disabled?: boolean;
  onChange?: (checked: boolean) => void;
  itemDescricao?: string;
  layout?: "inline" | "stacked" | "tabela";
};

function trackClass(estado: ProducaoToggleEstado, checked: boolean): string {
  if (checked) {
    return estado === "assinada"
      ? "bg-emerald-600"
      : "bg-emerald-500";
  }
  if (estado === "nao_produzido") {
    return "bg-amber-400";
  }
  if (estado === "nao_produzido_unidade") {
    return "bg-slate-400";
  }
  return "bg-neutral-300";
}

const ProducaoToggle: React.FC<ProducaoToggleProps> = ({
  checked,
  estado,
  disabled = false,
  onChange,
  itemDescricao,
  layout = "inline",
}) => {
  const label =
    estado === "nao_produzido_unidade"
      ? "Não produziu"
      : checked
        ? "Produziu"
        : layout === "stacked"
          ? "Marque para PRODUZIDO"
          : "Não produziu";
  const title =
    estado === "nao_produzido_unidade"
      ? "Não produziu — justificativa informada"
      : estado === "nao_produzido" && !checked
      ? "Não produziu — justificativa na observação"
      : checked
        ? "Marcado como produzido"
        : "Desligado — marque se produziu";

  const handleClick = () => {
    if (disabled || !onChange) return;
    onChange(!checked);
  };

  const switchControl = (
    <span
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors duration-200 ${trackClass(
        estado,
        checked
      )} ${disabled ? "opacity-60" : ""}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? "translate-x-6" : "translate-x-1"
        }`}
      />
    </span>
  );

  const labelBadgeClass =
    checked
      ? "bg-emerald-100 text-emerald-800"
      : estado === "nao_produzido"
        ? "bg-amber-100 text-amber-900"
        : estado === "nao_produzido_unidade"
          ? "bg-slate-200 text-slate-800"
          : "bg-neutral-200 text-neutral-600";

  if (layout === "tabela") {
    return (
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={
          itemDescricao ? `${label} — ${itemDescricao}` : `Produção: ${label}`
        }
        title={title}
        disabled={disabled}
        onClick={handleClick}
        className={`inline-flex flex-col items-center gap-1 ${
          disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
        }`}
      >
        {switchControl}
        <span
          className={`rounded-full px-2 py-0.5 text-[10px] font-semibold leading-tight ${labelBadgeClass}`}
        >
          {label}
        </span>
      </button>
    );
  }

  if (layout === "stacked") {
    return (
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={
          itemDescricao
            ? `${label} — ${itemDescricao}`
            : `Produção: ${label}`
        }
        title={title}
        disabled={disabled}
        onClick={handleClick}
        className={`flex w-full min-h-12 items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors touch-manipulation ${
          disabled
            ? "cursor-not-allowed border-neutral-200 bg-neutral-50"
            : "cursor-pointer border-neutral-200 bg-white active:bg-neutral-50"
        } ${checked ? "border-emerald-200 bg-emerald-50/40" : estado === "nao_produzido" ? "border-amber-200 bg-amber-50/40" : estado === "nao_produzido_unidade" ? "border-slate-300 bg-slate-50" : ""}`}
      >
        <span
          className={`text-xs font-semibold ${
            checked
              ? "text-emerald-800"
              : estado === "nao_produzido"
                ? "text-amber-900"
                : estado === "nao_produzido_unidade"
                  ? "text-slate-800"
                  : "text-neutral-600"
          }`}
        >
          {label}
        </span>
        {switchControl}
      </button>
    );
  }

  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={
        itemDescricao ? `${label} — ${itemDescricao}` : `Produção: ${label}`
      }
      title={title}
      disabled={disabled}
      onClick={handleClick}
      className={`inline-flex flex-col items-center gap-1 ${
        disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
      }`}
    >
      {switchControl}
      <span
        className={`max-w-[5.5rem] text-center text-[10px] font-semibold leading-tight ${
          checked
            ? "text-emerald-800"
            : estado === "nao_produzido"
              ? "text-amber-900"
              : estado === "nao_produzido_unidade"
                ? "text-slate-800"
                : "text-neutral-600"
        }`}
      >
        {label}
      </span>
    </button>
  );
};

export default ProducaoToggle;
